import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { tasksService } from '../../services/supabase/tasks';
import { Task } from '../../types';

const transformTask = (t: any): Task => ({
  id: t.id,
  // profile_id moved off the tasks table; getTasks joins routines so we
  // read it from the embedded relation when available.
  profileId: t.routines?.profile_id ?? t.profile_id ?? '',
  routineId: t.routine_id,
  label: t.label,
  icon: t.icon ?? '',
  color: t.color ?? '',
  start: (t.start_time || '00:00').slice(0, 5),
  end: (t.end_time || '00:00').slice(0, 5),
  order_index: t.order_index,
});

export function useTasks(profileId: string | null) {
  const queryClient = useQueryClient();

  const { data: tasks = [], isLoading, error } = useQuery({
    queryKey: ['tasks', profileId],
    queryFn: () => tasksService.getTasks(profileId!),
    enabled: !!profileId,
    select: (data) => data.map(transformTask)
  });

  // Tasks belong to a routine; the routine carries profile_id and scheduling.
  const createMutation = useMutation({
    mutationFn: (task: Partial<Task>) => {
      if (!profileId) throw new Error('No profile ID');
      if (!task.routineId) throw new Error('No routine ID - tasks must be associated with a routine');
      if (!task.label) throw new Error('Task label is required');
      if (!task.start) throw new Error('Task start time is required');
      if (!task.end) throw new Error('Task end time is required');

      const taskInsert: Parameters<typeof tasksService.createTask>[0] = {
        id: crypto.randomUUID(),
        routine_id: task.routineId,
        label: task.label,
        start_time: task.start,
        end_time: task.end,
      };

      if (task.icon) taskInsert.icon = task.icon;
      if (task.color) taskInsert.color = task.color;
      if (task.order_index !== undefined) taskInsert.order_index = task.order_index;

      return tasksService.createTask(taskInsert);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks', profileId] });
    }
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: Partial<Task> }) => {
      const dbUpdates: any = {};
      if (updates.label !== undefined) dbUpdates.label = updates.label;
      if (updates.icon !== undefined) dbUpdates.icon = updates.icon;
      if (updates.color !== undefined) dbUpdates.color = updates.color;
      if (updates.start !== undefined) dbUpdates.start_time = updates.start;
      if (updates.end !== undefined) dbUpdates.end_time = updates.end;
      if (updates.order_index !== undefined) dbUpdates.order_index = updates.order_index;

      return tasksService.updateTask(id, dbUpdates);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks', profileId] });
    }
  });

  const deleteMutation = useMutation({
    mutationFn: tasksService.deleteTask,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks', profileId] });
    }
  });

  return {
    tasks,
    loading: isLoading,
    error,
    createTask: createMutation.mutateAsync,
    updateTask: updateMutation.mutateAsync,
    deleteTask: deleteMutation.mutateAsync
  };
}
